import { z } from "zod";

import type { ManuscriptTemplate } from "./templates.ts";

export type HeadingLevel = 1 | 2 | 3;

export type StructuredAbstractKey =
  | "background"
  | "materialsAndMethods"
  | "results"
  | "conclusion";

export type StructuredAbstractContent = Record<StructuredAbstractKey, string>;
export type SubmissionRoute = "generic" | "journal";

export type ManuscriptSection = {
  id: string;
  title: string;
  level: HeadingLevel;
  parentId: string | null;
  content: string;
  role: "body" | "declarations" | "references";
};

export type ManuscriptTable = {
  id: string;
  title: string;
  notes: string;
  rows: string[][];
};

export type ManuscriptFigure = {
  id: string;
  title: string;
  legend: string;
};

export type ManuscriptMetadata = {
  title: string;
  runningTitle: string;
  authors: string;
  affiliations: string;
  correspondence: string;
  journal: string;
  articleType: string;
};

export type ManuscriptAffiliation = {
  id: string;
  name: string;
  ror: string;
};

export type ManuscriptAuthor = {
  id: string;
  name: string;
  degrees: string;
  email: string;
  orcid: string;
  affiliationIds: string[];
  corresponding: boolean;
  equalContribution: boolean;
};

export type ManuscriptReference = {
  id: string;
  text: string;
  doi: string;
  pmid: string;
  url: string;
};

export type Manuscript = {
  schemaVersion: 1;
  id: string;
  name: string;
  templateId: string;
  submissionRoute: SubmissionRoute;
  metadata: ManuscriptMetadata;
  authors: ManuscriptAuthor[];
  affiliations: ManuscriptAffiliation[];
  references: ManuscriptReference[];
  abstract: {
    mode: "unstructured" | "structured";
    content: string;
    structured: StructuredAbstractContent;
  };
  keywords: string;
  sections: ManuscriptSection[];
  tables: ManuscriptTable[];
  figures: ManuscriptFigure[];
  updatedAt: string;
};

export type ValidationSeverity = "blocker" | "warning" | "info";

export type ValidationItem = {
  code: string;
  severity: ValidationSeverity;
  message: string;
  sectionId?: string;
};

export type ManuscriptValidation = {
  canExport: boolean;
  items: ValidationItem[];
  counts: Record<ValidationSeverity, number>;
};

export type StructuredTextSegment = {
  text: string;
  bold: boolean;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ORCID_PATTERN = /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/;

export const STRUCTURED_ABSTRACT_FIELDS: ReadonlyArray<{
  key: StructuredAbstractKey;
  label: string;
  zhLabel: string;
}> = [
  { key: "background", label: "Background", zhLabel: "背景" },
  {
    key: "materialsAndMethods",
    label: "Materials and Methods",
    zhLabel: "材料和方法",
  },
  { key: "results", label: "Results", zhLabel: "结果" },
  { key: "conclusion", label: "Conclusion", zhLabel: "结论" },
];

const DECLARATION_REQUIREMENTS = [
  {
    code: "funding",
    label: "Funding（基金/经费）",
    title: /^(?:funding|financial disclosure)$/i,
    inline: /(?:^|\n)\s*(?:funding|financial disclosure)\s*[:：—-]\s*\S/i,
  },
  {
    code: "conflict-of-interest",
    label: "Conflict of interest（利益冲突）",
    title:
      /^(?:conflicts? of interest|competing interests|declaration of interests)$/i,
    inline:
      /(?:^|\n)\s*(?:conflicts? of interest|competing interests|declaration of interests)\s*[:：—-]\s*\S/i,
  },
  {
    code: "data-availability",
    label: "Data availability（数据可用性）",
    title:
      /^(?:data availability|availability of data(?: and materials)?|data and materials availability)$/i,
    inline:
      /(?:^|\n)\s*(?:data availability|availability of data(?: and materials)?|data and materials availability)\s*[:：—-]\s*\S/i,
  },
] as const;

export function createEmptyStructuredAbstract(): StructuredAbstractContent {
  return {
    background: "",
    materialsAndMethods: "",
    results: "",
    conclusion: "",
  };
}

function orcidCore(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\/(?:www\.)?orcid\.org\//i, "")
    .toUpperCase();
}

export function isPlausibleEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export function isValidOrcid(value: string): boolean {
  const core = orcidCore(value);
  if (!ORCID_PATTERN.test(core)) return false;
  const compact = core.replaceAll("-", "");
  let total = 0;
  for (const character of compact.slice(0, 15)) {
    total = (total + Number(character)) * 2;
  }
  const result = (12 - (total % 11)) % 11;
  const expected = result === 10 ? "X" : String(result);
  return compact.at(-1) === expected;
}

export function canonicalizeOrcid(value: string): string {
  return isValidOrcid(value) ? `https://orcid.org/${orcidCore(value)}` : "";
}

const boundedText = (maximum: number) => z.string().max(maximum);
const identifierSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[A-Za-z0-9._-]+$/);

const manuscriptSchema = z.object({
  schemaVersion: z.literal(1),
  id: identifierSchema,
  name: boundedText(500),
  templateId: identifierSchema,
  submissionRoute: z.enum(["generic", "journal"]).optional(),
  metadata: z.object({
    title: boundedText(1000),
    runningTitle: boundedText(500),
    authors: boundedText(20_000),
    affiliations: boundedText(30_000),
    correspondence: boundedText(20_000),
    journal: boundedText(1000),
    articleType: boundedText(500),
  }),
  authors: z
    .array(
      z.object({
        id: identifierSchema,
        name: boundedText(500),
        degrees: boundedText(500),
        email: boundedText(500),
        orcid: boundedText(100),
        affiliationIds: z.array(identifierSchema).max(100),
        corresponding: z.boolean(),
        equalContribution: z.boolean(),
      }),
    )
    .max(500)
    .superRefine((authors, context) => {
      const seen = new Set<string>();
      authors.forEach((author, index) => {
        if (seen.has(author.id)) {
          context.addIssue({
            code: "custom",
            message: "作者标识符不能重复",
            path: [index, "id"],
          });
        }
        seen.add(author.id);
      });
    })
    .default([]),
  affiliations: z
    .array(
      z.object({
        id: identifierSchema,
        name: boundedText(3000),
        ror: boundedText(500),
      }),
    )
    .max(500)
    .superRefine((affiliations, context) => {
      const seen = new Set<string>();
      affiliations.forEach((affiliation, index) => {
        if (seen.has(affiliation.id)) {
          context.addIssue({
            code: "custom",
            message: "机构标识符不能重复",
            path: [index, "id"],
          });
        }
        seen.add(affiliation.id);
      });
    })
    .default([]),
  references: z
    .array(
      z.object({
        id: identifierSchema,
        text: boundedText(20_000),
        doi: boundedText(500),
        pmid: boundedText(200),
        url: boundedText(3000),
      }),
    )
    .max(10_000)
    .superRefine((references, context) => {
      const seen = new Set<string>();
      references.forEach((reference, index) => {
        if (seen.has(reference.id)) {
          context.addIssue({
            code: "custom",
            message: "参考文献标识符不能重复",
            path: [index, "id"],
          });
        }
        seen.add(reference.id);
      });
    })
    .default([]),
  abstract: z.object({
    mode: z.enum(["unstructured", "structured"]),
    content: boundedText(200_000),
    structured: z
      .object({
        background: boundedText(100_000),
        materialsAndMethods: boundedText(100_000),
        results: boundedText(100_000),
        conclusion: boundedText(100_000),
      })
      .optional(),
  }),
  keywords: boundedText(20_000),
  sections: z
    .array(
      z.object({
        id: identifierSchema,
        title: boundedText(1000),
        level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        parentId: identifierSchema.nullable().optional(),
        content: boundedText(2_000_000),
        role: z.enum(["body", "declarations", "references"]),
      }),
    )
    .max(400)
    .superRefine((sections, context) => {
      const seen = new Set<string>();
      sections.forEach((section, index) => {
        if (seen.has(section.id)) {
          context.addIssue({
            code: "custom",
            message: "正文区块标识符不能重复",
            path: [index, "id"],
          });
        }
        seen.add(section.id);
      });
    }),
  tables: z
    .array(
      z.object({
        id: identifierSchema,
        title: boundedText(2000),
        notes: boundedText(20_000),
        rows: z
          .array(z.array(boundedText(20_000)).min(1).max(30))
          .min(1)
          .max(100),
      }),
    )
    .max(100)
    .superRefine((tables, context) => {
      const seen = new Set<string>();
      tables.forEach((table, index) => {
        if (seen.has(table.id)) {
          context.addIssue({
            code: "custom",
            message: "表格标识符不能重复",
            path: [index, "id"],
          });
        }
        seen.add(table.id);
      });
    })
    .optional(),
  figures: z
    .array(
      z.object({
        id: identifierSchema,
        title: boundedText(2000),
        legend: boundedText(50_000),
      }),
    )
    .max(100)
    .superRefine((figures, context) => {
      const seen = new Set<string>();
      figures.forEach((figure, index) => {
        if (seen.has(figure.id)) {
          context.addIssue({
            code: "custom",
            message: "图注标识符不能重复",
            path: [index, "id"],
          });
        }
        seen.add(figure.id);
      });
    })
    .optional(),
  updatedAt: boundedText(100),
});

const DEFAULT_SECTIONS: ReadonlyArray<
  Pick<ManuscriptSection, "id" | "title" | "level" | "parentId" | "role">
> = [
  {
    id: "introduction",
    title: "Introduction",
    level: 1,
    parentId: null,
    role: "body",
  },
  {
    id: "materials-methods",
    title: "Materials and Methods",
    level: 1,
    parentId: null,
    role: "body",
  },
  { id: "results", title: "Results", level: 1, parentId: null, role: "body" },
  {
    id: "discussion",
    title: "Discussion",
    level: 1,
    parentId: null,
    role: "body",
  },
  {
    id: "conclusions",
    title: "Conclusions",
    level: 1,
    parentId: null,
    role: "body",
  },
  {
    id: "declarations",
    title: "Declarations",
    level: 1,
    parentId: null,
    role: "declarations",
  },
  {
    id: "declaration-funding",
    title: "Funding",
    level: 2,
    parentId: "declarations",
    role: "declarations",
  },
  {
    id: "declaration-conflict",
    title: "Conflict of interest",
    level: 2,
    parentId: "declarations",
    role: "declarations",
  },
  {
    id: "declaration-data-availability",
    title: "Data availability",
    level: 2,
    parentId: "declarations",
    role: "declarations",
  },
];

function makeId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createInitialManuscript(now = new Date()): Manuscript {
  const authors = Array.from({ length: 5 }, (_, index) => ({
    ...createAuthor(),
    corresponding: index === 1,
  }));
  return {
    schemaVersion: 1,
    id: makeId("manuscript"),
    name: "未命名稿件",
    templateId: "sci-conservative",
    submissionRoute: "generic",
    metadata: {
      title: "",
      runningTitle: "",
      authors: "",
      affiliations: "",
      correspondence: "",
      journal: "",
      articleType: "",
    },
    authors,
    affiliations: [createAffiliation()],
    references: [],
    abstract: {
      mode: "unstructured",
      content: "",
      structured: createEmptyStructuredAbstract(),
    },
    keywords: "",
    sections: DEFAULT_SECTIONS.map((section) => ({
      ...section,
      content: "",
    })),
    tables: [],
    figures: [],
    updatedAt: now.toISOString(),
  };
}

export function parseManuscript(value: unknown): Manuscript {
  const parsed = manuscriptSchema.parse(value);
  const authors = parsed.authors.length
    ? parsed.authors
    : parsed.metadata.authors
        .split(/[;\n]+/)
        .map((name) => name.trim())
        .filter(Boolean)
        .map((name, index) => ({
          ...createAuthor(),
          id: `legacy-author-${index + 1}`,
          name,
        }));
  const affiliations = parsed.affiliations.length
    ? parsed.affiliations
    : parsed.metadata.affiliations
        .split(/\n+/)
        .map((name) => name.trim())
        .filter(Boolean)
        .map((name, index) => ({
          ...createAffiliation(),
          id: `legacy-affiliation-${index + 1}`,
          name,
        }));
  const legacyReferenceText = parsed.sections
    .filter((section) => section.role === "references")
    .map((section) => section.content)
    .filter(Boolean)
    .join("\n\n");
  const references = parsed.references.length
    ? parsed.references
    : splitReferencePaste(legacyReferenceText).map((text, index) => ({
        ...createReference(),
        id: `legacy-reference-${index + 1}`,
        text,
      }));
  const structured = parsed.abstract.structured ??
    parseLegacyStructuredAbstract(parsed.abstract.content);
  const sections = normalizeSectionHierarchy(
    parsed.sections
      .filter((section) => section.role !== "references")
      .map((section) => ({ ...section, parentId: section.parentId ?? null })),
  );

  return {
    ...parsed,
    submissionRoute:
      parsed.submissionRoute ??
      (parsed.metadata.journal.trim() ? "journal" : "generic"),
    authors,
    affiliations,
    references,
    abstract: { ...parsed.abstract, structured },
    sections,
    tables: parsed.tables ?? [],
    figures: parsed.figures ?? [],
  };
}

export function createSection(
  level: HeadingLevel,
  title = "New section",
  parentId: string | null = null,
): ManuscriptSection {
  return {
    id: makeId("section"),
    title,
    level,
    parentId,
    content: "",
    role: "body",
  };
}

export function createManuscriptTable(
  rows: string[][] = [["", ""], ["", ""]],
): ManuscriptTable {
  return {
    id: makeId("table"),
    title: "",
    notes: "",
    rows: normalizeTableRows(rows),
  };
}

export function createManuscriptFigure(): ManuscriptFigure {
  return {
    id: makeId("figure"),
    title: "",
    legend: "",
  };
}

export function createAuthor(): ManuscriptAuthor {
  return {
    id: makeId("author"),
    name: "",
    degrees: "",
    email: "",
    orcid: "",
    affiliationIds: [],
    corresponding: false,
    equalContribution: false,
  };
}

export function createAffiliation(): ManuscriptAffiliation {
  return {
    id: makeId("affiliation"),
    name: "",
    ror: "",
  };
}

export function createReference(): ManuscriptReference {
  return {
    id: makeId("reference"),
    text: "",
    doi: "",
    pmid: "",
    url: "",
  };
}

export function parseLegacyStructuredAbstract(
  content: string,
): StructuredAbstractContent {
  const result = createEmptyStructuredAbstract();
  const pattern =
    /(?:^|\n)\s*(Background|Materials\s+and\s+Methods|Methods\s+and\s+Materials|Materials?|Methods?|Results?|Conclusions?)\s*:\s*/gi;
  const matches = [...content.matchAll(pattern)];
  matches.forEach((match, index) => {
    const label = (match[1] ?? "").toLowerCase().replace(/\s+/g, " ");
    const start = (match.index ?? 0) + match[0].length;
    const end = matches[index + 1]?.index ?? content.length;
    const value = content.slice(start, end).trim();
    const key: StructuredAbstractKey = label.startsWith("background")
      ? "background"
      : label.startsWith("result")
        ? "results"
        : label.startsWith("conclusion")
          ? "conclusion"
          : "materialsAndMethods";
    result[key] = [result[key], value].filter(Boolean).join("\n\n");
  });
  return result;
}

export function structuredAbstractHasContent(
  abstract: Manuscript["abstract"],
): boolean {
  if (abstract.mode === "unstructured") return Boolean(abstract.content.trim());
  return (
    STRUCTURED_ABSTRACT_FIELDS.some(({ key }) =>
      Boolean(abstract.structured[key].trim()),
    ) || Boolean(abstract.content.trim())
  );
}

export function structuredAbstractToText(
  abstract: Manuscript["abstract"],
): string {
  if (abstract.mode === "unstructured") return abstract.content;
  const hasSeparatedContent = STRUCTURED_ABSTRACT_FIELDS.some(({ key }) =>
    Boolean(abstract.structured[key].trim()),
  );
  if (!hasSeparatedContent) return abstract.content;
  return STRUCTURED_ABSTRACT_FIELDS.map(
    ({ key, label }) => `${label}: ${abstract.structured[key].trim()}`.trimEnd(),
  ).join("\n");
}

export function normalizeSectionHierarchy(
  sections: ManuscriptSection[],
): ManuscriptSection[] {
  const normalized: ManuscriptSection[] = [];
  let currentH1: ManuscriptSection | undefined;
  let currentH2: ManuscriptSection | undefined;
  for (const section of sections) {
    if (section.level === 1) {
      const next = { ...section, parentId: null };
      normalized.push(next);
      currentH1 = next;
      currentH2 = undefined;
      continue;
    }
    if (section.level === 2) {
      const suppliedParent = normalized.find(
        (item) => item.id === section.parentId && item.level === 1,
      );
      const next = {
        ...section,
        parentId:
          suppliedParent && suppliedParent.id === currentH1?.id
            ? suppliedParent.id
            : (currentH1?.id ?? null),
      };
      normalized.push(next);
      currentH2 = next;
      continue;
    }
    const suppliedParent = normalized.find(
      (item) => item.id === section.parentId && item.level === 2,
    );
    normalized.push({
      ...section,
      parentId:
        suppliedParent && suppliedParent.id === currentH2?.id
          ? suppliedParent.id
          : (currentH2?.id ?? null),
    });
  }
  return normalized;
}

function sectionIsDescendantOf(
  section: ManuscriptSection,
  ancestorId: string,
  byId: Map<string, ManuscriptSection>,
): boolean {
  let parentId = section.parentId;
  while (parentId) {
    if (parentId === ancestorId) return true;
    parentId = byId.get(parentId)?.parentId ?? null;
  }
  return false;
}

function sectionSubtreeEndIndex(
  sections: ManuscriptSection[],
  startIndex: number,
): number {
  const root = sections[startIndex];
  if (!root) return startIndex;
  const byId = new Map(sections.map((section) => [section.id, section]));
  let end = startIndex + 1;
  while (
    end < sections.length &&
    sectionIsDescendantOf(sections[end], root.id, byId)
  ) {
    end += 1;
  }
  return end;
}

export function insertChildSection(
  sections: ManuscriptSection[],
  section: ManuscriptSection,
): ManuscriptSection[] {
  if (!section.parentId) return [...sections, section];
  const parentIndex = sections.findIndex((item) => item.id === section.parentId);
  if (parentIndex < 0) return sections;
  const parent = sections[parentIndex];
  if (section.level !== parent.level + 1) return sections;
  const insertAt = sectionSubtreeEndIndex(sections, parentIndex);
  const next = [...sections];
  next.splice(insertAt, 0, section);
  return next;
}

export function createSectionForParent(
  sections: ManuscriptSection[],
  parentId: string | null,
  title?: string,
): ManuscriptSection | null {
  const parent = parentId
    ? sections.find((section) => section.id === parentId)
    : undefined;
  if (parentId && !parent) return null;
  if (parent?.level === 3) return null;
  const level = parent ? ((parent.level + 1) as 2 | 3) : 1;
  const defaultTitle =
    level === 1
      ? "New section"
      : level === 2
        ? "New subsection"
        : "New detail";
  return {
    ...createSection(level, title ?? defaultTitle, parent?.id ?? null),
    role: parent?.role ?? "body",
  };
}

export function insertSectionAfterTree(
  sections: ManuscriptSection[],
  section: ManuscriptSection,
  afterId?: string,
): ManuscriptSection[] {
  if (section.level !== 1) return sections;
  if (!afterId) return [...sections, { ...section, parentId: null }];
  const afterIndex = sections.findIndex((item) => item.id === afterId);
  if (afterIndex < 0) return [...sections, { ...section, parentId: null }];
  const insertAt = sectionSubtreeEndIndex(sections, afterIndex);
  const next = [...sections];
  next.splice(insertAt, 0, { ...section, parentId: null });
  return next;
}

export function sectionSiblingPosition(
  sections: ManuscriptSection[],
  id: string,
): { index: number; count: number } {
  const current = sections.find((section) => section.id === id);
  if (!current) return { index: -1, count: 0 };
  const siblings = sections.filter(
    (section) =>
      section.level === current.level && section.parentId === current.parentId,
  );
  return {
    index: siblings.findIndex((section) => section.id === id),
    count: siblings.length,
  };
}

export function moveSectionById(
  sections: ManuscriptSection[],
  id: string,
  direction: -1 | 1,
): ManuscriptSection[] {
  const currentIndex = sections.findIndex((section) => section.id === id);
  if (currentIndex < 0) return sections;
  const current = sections[currentIndex];
  const siblings = sections.filter(
    (section) =>
      section.level === current.level && section.parentId === current.parentId,
  );
  const siblingIndex = siblings.findIndex((section) => section.id === id);
  const target = siblings[siblingIndex + direction];
  if (!target) return sections;

  const currentEnd = sectionSubtreeEndIndex(sections, currentIndex);
  const targetIndex = sections.findIndex((section) => section.id === target.id);
  const targetEnd = sectionSubtreeEndIndex(sections, targetIndex);
  if (direction === -1) {
    return [
      ...sections.slice(0, targetIndex),
      ...sections.slice(currentIndex, currentEnd),
      ...sections.slice(targetIndex, currentIndex),
      ...sections.slice(currentEnd),
    ];
  }
  return [
    ...sections.slice(0, currentIndex),
    ...sections.slice(targetIndex, targetEnd),
    ...sections.slice(currentIndex, currentEnd),
    ...sections.slice(targetEnd),
  ];
}

export function removeSectionTreeById(
  sections: ManuscriptSection[],
  id: string,
): ManuscriptSection[] {
  const byId = new Map(sections.map((section) => [section.id, section]));
  return sections.filter(
    (section) =>
      section.id !== id && !sectionIsDescendantOf(section, id, byId),
  );
}

function parseCsvRow(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === "," && !quoted) {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += character;
    }
  }
  cells.push(cell.trim());
  return cells;
}

export function normalizeTableRows(rows: string[][]): string[][] {
  const safeRows = rows
    .slice(0, 100)
    .map((row) => row.slice(0, 30).map((cell) => String(cell ?? "").trim()));
  const columnCount = Math.max(0, ...safeRows.map((row) => row.length));
  if (!safeRows.length || columnCount < 2) return [];
  return safeRows.map((row) => [
    ...row,
    ...Array.from({ length: columnCount - row.length }, () => ""),
  ]);
}

export function parsePastedTable(text: string): string[][] {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return [];
  let rows: string[][];
  if (lines.some((line) => line.includes("\t"))) {
    rows = lines.map((line) => line.split("\t"));
  } else if (lines.every((line) => line.includes("|"))) {
    rows = lines
      .map((line) => line.replace(/^\|/, "").replace(/\|$/, "").split("|"))
      .filter(
        (row) =>
          !row.every((cell) => /^\s*:?-{3,}:?\s*$/.test(cell)),
      );
  } else if (lines.every((line) => line.includes(","))) {
    rows = lines.map(parseCsvRow);
  } else {
    return [];
  }
  return normalizeTableRows(rows);
}

export function moveItemById<T extends { id: string }>(
  items: T[],
  id: string,
  direction: -1 | 1,
): T[] {
  const index = items.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function synchronizeFirstAuthorContribution(
  authors: ManuscriptAuthor[],
): ManuscriptAuthor[] {
  if (!authors.length) return authors;
  const hasCoFirst = authors
    .slice(1)
    .some((author) => author.equalContribution);
  return authors.map((author, index) =>
    index === 0
      ? {
          ...author,
          equalContribution: hasCoFirst,
        }
      : author,
  );
}

export function appendAuthor(
  authors: ManuscriptAuthor[],
  author = createAuthor(),
): ManuscriptAuthor[] {
  return [...authors, author];
}

export function updateAuthorById(
  authors: ManuscriptAuthor[],
  id: string,
  patch: Partial<Omit<ManuscriptAuthor, "id">>,
): ManuscriptAuthor[] {
  const index = authors.findIndex((author) => author.id === id);
  if (index < 0) return authors;
  return authors.map((author) =>
    author.id === id ? { ...author, ...patch } : author,
  );
}

export function moveAuthorById(
  authors: ManuscriptAuthor[],
  id: string,
  direction: -1 | 1,
): ManuscriptAuthor[] {
  const index = authors.findIndex((author) => author.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= authors.length) return authors;

  return moveItemById(authors, id, direction);
}

export function setCoFirstAuthor(
  authors: ManuscriptAuthor[],
  id: string,
  checked: boolean,
): ManuscriptAuthor[] {
  const index = authors.findIndex((author) => author.id === id);
  if (index <= 0) return authors;
  return synchronizeFirstAuthorContribution(
    authors.map((author) =>
      author.id === id ? { ...author, equalContribution: checked } : author,
    ),
  );
}

export function setCoCorrespondingAuthor(
  authors: ManuscriptAuthor[],
  id: string,
  checked: boolean,
): ManuscriptAuthor[] {
  const index = authors.findIndex((author) => author.id === id);
  if (index < 0) return authors;
  return authors.map((author) =>
    author.id === id ? { ...author, corresponding: checked } : author,
  );
}

export function removeAuthorById(
  authors: ManuscriptAuthor[],
  id: string,
): ManuscriptAuthor[] {
  return authors.filter((author) => author.id !== id);
}

export function removeAffiliationById(
  affiliations: ManuscriptAffiliation[],
  authors: ManuscriptAuthor[],
  id: string,
): Pick<Manuscript, "affiliations" | "authors"> {
  return {
    affiliations: affiliations.filter((affiliation) => affiliation.id !== id),
    authors: authors.map((author) => ({
      ...author,
      affiliationIds: author.affiliationIds.filter(
        (affiliationId) => affiliationId !== id,
      ),
    })),
  };
}

export function splitReferencePaste(value: string): string[] {
  const text = value.trim();
  if (!text) return [];
  const blocks = text
    .split(/\n\s*\n+/)
    .map((item) => item.replace(/\s*\n\s*/g, " ").trim())
    .filter(Boolean);
  if (blocks.length > 1) return blocks;
  const numberedLines = text
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return numberedLines.length > 1 &&
    numberedLines.every((item) => /^\[?\d+[\].)]\s+/.test(item))
    ? numberedLines
    : blocks;
}

export function formatReferenceEntry(
  reference: ManuscriptReference,
  index: number,
  numbering: "preserve" | "numeric",
): string {
  const supplied = reference.text.trim();
  const doi = reference.doi
    .trim()
    .replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "");
  const pmid = reference.pmid.trim();
  const url = reference.url.trim();
  const suppliedLower = supplied.toLocaleLowerCase();
  const identifiers: string[] = [];
  if (doi && !suppliedLower.includes(doi.toLocaleLowerCase())) {
    identifiers.push(`doi:${doi}`);
  }
  if (
    pmid &&
    !new RegExp(`\\bPMID\\s*:\\s*${pmid.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(
      supplied,
    )
  ) {
    identifiers.push(`PMID:${pmid}`);
  }
  const normalizedUrlDoi = url.replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "");
  if (
    url &&
    !suppliedLower.includes(url.toLocaleLowerCase()) &&
    !(doi && normalizedUrlDoi.toLocaleLowerCase() === doi.toLocaleLowerCase())
  ) {
    identifiers.push(url);
  }
  const content = [supplied, ...identifiers].filter(Boolean).join(" ");
  if (numbering === "preserve" || !content) return content;
  const withoutExistingNumber = content.replace(/^\s*(?:\[\d+\]|\d+[.)])\s*/, "");
  return `${index + 1}. ${withoutExistingNumber}`;
}

export function authorPositionLabel(index: number): string {
  if (index === 0) return "第一作者";
  if (index === 1) return "第二作者";
  return `第 ${index + 1} 作者`;
}

export function countWords(text: string): number {
  const latinWords = text.match(/[A-Za-z0-9]+(?:['’-][A-Za-z0-9]+)*/g) ?? [];
  const cjkCharacters = text.match(/[\u3400-\u9fff]/g) ?? [];
  return latinWords.length + cjkCharacters.length;
}

export function tokenizeStructuredLabels(text: string): StructuredTextSegment[] {
  const pattern =
    /\b(?:Abstract|Background|Importance|Aim|Aims|Objective|Objectives|Purpose|Design|Setting|Participants|Exposures?|Interventions?|Methods?|Materials?|Materials and methods|Methods and materials|Main outcomes? and measures?|Results?|Findings|Discussion|Limitations|Conclusions?|Relevance|Keywords?|Key words)\s*:/gi;
  const segments: StructuredTextSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      segments.push({ text: text.slice(cursor, index), bold: false });
    }
    segments.push({ text: match[0], bold: true });
    cursor = index + match[0].length;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), bold: false });
  }
  return segments.length ? segments : [{ text, bold: false }];
}

export function numberSections(
  sections: ManuscriptSection[],
  mode: "none" | "decimal",
): Array<ManuscriptSection & { displayTitle: string; number: string }> {
  const numbers = new Map<string, string>();
  const siblingCounts = new Map<string, number>();

  return sections.map((section) => {
    if (mode === "none" || section.role !== "body") {
      return { ...section, number: "", displayTitle: section.title };
    }

    const siblingKey = section.parentId ?? "root";
    const siblingNumber = (siblingCounts.get(siblingKey) ?? 0) + 1;
    siblingCounts.set(siblingKey, siblingNumber);
    const parentNumber = section.parentId
      ? (numbers.get(section.parentId) ?? "")
      : "";
    const number = parentNumber
      ? `${parentNumber}.${siblingNumber}`
      : String(siblingNumber);
    numbers.set(section.id, number);
    return {
      ...section,
      number,
      displayTitle: `${number} ${section.title}`.trim(),
    };
  });
}

function numericCitationNumbers(manuscript: Manuscript): number[] {
  const numbers = new Set<number>();
  const text = manuscript.sections
    .filter((section) => section.role !== "references")
    .map((section) => section.content)
    .join("\n");
  for (const match of text.matchAll(/\[(\d+(?:\s*[-,;]\s*\d+)*)\]/g)) {
    for (const token of match[1].match(/\d+/g) ?? []) {
      const value = Number(token);
      if (Number.isInteger(value) && value > 0) numbers.add(value);
    }
  }
  return [...numbers].sort((left, right) => left - right);
}

function hasSuppliedNumericPrefix(reference: ManuscriptReference): boolean {
  return /^\s*(?:\[\d+\]|\d+[.)])\s+/.test(reference.text);
}

export function validateManuscript(
  manuscript: Manuscript,
  template?: ManuscriptTemplate,
): ManuscriptValidation {
  const items: ValidationItem[] = [];

  if (manuscript.submissionRoute === "journal") {
    if (!manuscript.metadata.journal.trim()) {
      items.push({
        code: "missing-target-journal",
        severity: "warning",
        message: "已选择指定期刊路线，但尚未填写目标期刊名称。",
      });
    }
    if (!manuscript.metadata.articleType.trim()) {
      items.push({
        code: "missing-article-type",
        severity: "warning",
        message: "指定期刊投稿前还需填写文章类型并核对对应作者指南。",
      });
    }
    items.push({
      code: "unverified-journal-template",
      severity: "warning",
      message:
        "指定期刊模板将在导出时由服务端注册表核验；页面不能依据客户端字段自行确认，核验前只能作为待核验稿。",
    });
  }

  if (!manuscript.metadata.title.trim()) {
    items.push({
      code: "missing-title",
      severity: "blocker",
      message: "题名为空；为防止生成无法识别的文件，至少需要填写题名。",
    });
  }
  manuscript.authors.forEach((author, index) => {
    if (
      !author.name.trim() &&
      (author.degrees.trim() ||
        author.email.trim() ||
        author.orcid.trim() ||
        author.affiliationIds.length ||
        author.equalContribution)
    ) {
      items.push({
        code: "missing-author-name",
        severity: "blocker",
        message: `${authorPositionLabel(index)}已有详细信息，但姓名为空。`,
      });
    }
  });
  const structuredAuthors = manuscript.authors.filter((author) => author.name.trim());
  if (!structuredAuthors.length && !manuscript.metadata.authors.trim()) {
    items.push({
      code: "missing-authors",
      severity: "warning",
      message: "作者信息尚未填写；软件不会自动虚构作者。",
    });
  }
  if (structuredAuthors.length) {
    const affiliationIds = new Set(
      manuscript.affiliations
        .filter((affiliation) => affiliation.name.trim())
        .map((affiliation) => affiliation.id),
    );
    const correspondingAuthors = structuredAuthors.filter(
      (author) => author.corresponding,
    );
    if (!correspondingAuthors.length) {
      items.push({
        code: "missing-corresponding-author",
        severity: "warning",
        message: "尚未指定通讯作者；请按目标期刊要求核对。",
      });
    } else if (correspondingAuthors.length > 1) {
      items.push({
        code: "multiple-corresponding-authors",
        severity: "warning",
        message:
          "已标记多位通讯作者；部分期刊只接受一位，请按目标期刊最新作者指南核对。",
      });
    }
    const seenOrcids = new Map<string, string>();
    for (const author of structuredAuthors) {
      if (author.corresponding && !author.email.trim()) {
        items.push({
          code: "missing-corresponding-email",
          severity: "blocker",
          message: `通讯作者“${author.name}”尚未填写邮箱。`,
        });
      } else if (author.email.trim() && !isPlausibleEmail(author.email)) {
        items.push({
          code: "invalid-author-email",
          severity: "blocker",
          message: `作者“${author.name}”的邮箱格式不完整。`,
        });
      }
      if (author.orcid.trim() && !isValidOrcid(author.orcid)) {
        items.push({
          code: "invalid-orcid",
          severity: "blocker",
          message: `作者“${author.name}”的 ORCID 格式或校验位不正确。`,
        });
      } else if (author.orcid.trim()) {
        const canonical = canonicalizeOrcid(author.orcid);
        const existing = seenOrcids.get(canonical);
        if (existing) {
          items.push({
            code: "duplicate-orcid",
            severity: "blocker",
            message: `作者“${existing}”与“${author.name}”使用了同一个 ORCID。`,
          });
        } else {
          seenOrcids.set(canonical, author.name);
        }
      }
      if (!author.affiliationIds.some((id) => affiliationIds.has(id))) {
        items.push({
          code: "unlinked-author-affiliation",
          severity: "warning",
          message: `作者“${author.name}”尚未关联有效机构。`,
        });
      }
    }
    if (structuredAuthors.filter((author) => author.equalContribution).length === 1) {
      items.push({
        code: "single-equal-contributor",
        severity: "warning",
        message: "只有一名作者被标记为同等贡献；共同第一/同等贡献通常至少涉及两人。",
      });
    }
  }
  if (!structuredAbstractHasContent(manuscript.abstract)) {
    items.push({
      code: "missing-abstract",
      severity: "warning",
      message: "摘要为空。",
    });
  } else if (manuscript.abstract.mode === "structured") {
    const separatedFieldsPresent = STRUCTURED_ABSTRACT_FIELDS.some(({ key }) =>
      Boolean(manuscript.abstract.structured[key].trim()),
    );
    if (separatedFieldsPresent) {
      STRUCTURED_ABSTRACT_FIELDS.forEach(({ key, zhLabel }) => {
        if (!manuscript.abstract.structured[key].trim()) {
          items.push({
            code: `missing-structured-abstract-${key}`,
            severity: "warning",
            message: `结构式摘要的“${zhLabel}”尚未填写。`,
          });
        }
      });
    } else if (
      !tokenizeStructuredLabels(manuscript.abstract.content).some(
        (segment) => segment.bold,
      )
    ) {
      items.push({
        code: "structured-abstract-without-labels",
        severity: "warning",
        message:
          "旧结构式摘要尚未识别到标签；原文已保留，请拆分到四个独立输入框。",
      });
    }
  }
  if (!manuscript.keywords.trim()) {
    items.push({
      code: "missing-keywords",
      severity: "warning",
      message: "关键词为空。",
    });
  }
  const populatedReferences = manuscript.references.filter((reference) =>
    [reference.text, reference.doi, reference.pmid, reference.url].some((value) =>
      value.trim(),
    ),
  );
  if (!populatedReferences.length) {
    items.push({
      code: "missing-references",
      severity: "warning",
      message: "参考文献尚未填写；软件不会自动生成或虚构引文。",
    });
  }
  const numericCitations = numericCitationNumbers(manuscript);
  if (numericCitations.length && populatedReferences.length) {
    const highestCitation = numericCitations.at(-1) ?? 0;
    if (highestCitation > populatedReferences.length) {
      items.push({
        code: "numeric-citation-missing-reference",
        severity: "warning",
        message: `正文数字引文最高到 [${highestCitation}]，但仅录入 ${populatedReferences.length} 条参考文献。`,
      });
    }
    if (
      template?.references.numbering !== "numeric" &&
      !populatedReferences.every(hasSuppliedNumericPrefix)
    ) {
      items.push({
        code: "numeric-citation-unnumbered-references",
        severity: "warning",
        message:
          "正文存在数字引文，但参考文献未按数字编号输出；请切换数字编号或保留来源中的编号。",
      });
    }
  }

  const declarationSections = manuscript.sections.filter(
    (section) => section.role === "declarations",
  );
  for (const requirement of DECLARATION_REQUIREMENTS) {
    const supplied = declarationSections.some(
      (section) =>
        (requirement.title.test(section.title.trim()) && Boolean(section.content.trim())) ||
        requirement.inline.test(section.content),
    );
    if (!supplied) {
      items.push({
        code: `missing-declaration-${requirement.code}`,
        severity: "warning",
        message: `${requirement.label}尚未填写；请按真实情况填写，不要使用虚构占位内容。`,
        sectionId: "declarations",
      });
    }
  }

  for (const section of manuscript.sections) {
    if (!section.title.trim()) {
      items.push({
        code: "missing-section-title",
        severity: "blocker",
        message: "存在没有标题的正文区块。",
        sectionId: section.id,
      });
    } else if (
      !section.content.trim() &&
      section.role === "body" &&
      !manuscript.sections.some((item) => item.parentId === section.id)
    ) {
      items.push({
        code: "empty-section",
        severity: "warning",
        message: `“${section.title}”尚未填写正文。`,
        sectionId: section.id,
      });
    }
    if (section.level > 1 && !section.parentId) {
      items.push({
        code: "orphan-subheading",
        severity: "blocker",
        message: `“${section.title || "未命名小标题"}”没有所属的上级标题。`,
        sectionId: section.id,
      });
    }
  }

  manuscript.tables.forEach((table) => {
    if (!table.title.trim()) {
      items.push({
        code: "missing-table-title",
        severity: "warning",
        message: "存在未填写标题的三线表。",
        sectionId: table.id,
      });
    }
  });

  const bodyText = manuscript.sections
    .filter((section) => section.role !== "references")
    .map((section) => section.content)
    .join("\n");
  manuscript.figures.forEach((figure, index) => {
    if (!figure.title.trim()) {
      items.push({
        code: "missing-figure-title",
        severity: "warning",
        message: `图 ${index + 1} 尚未填写图题。`,
        sectionId: figure.id,
      });
    }
    if (
      (figure.title.trim() || figure.legend.trim()) &&
      !new RegExp(
        "\\b(?:Figure|Fig\\.)\\s*" + (index + 1) + "\\b",
        "i",
      ).test(bodyText)
    ) {
      items.push({
        code: "uncited-figure",
        severity: "warning",
        message: `图 ${index + 1} 尚未在正文中以 Figure ${index + 1} 或 Fig. ${index + 1} 引用。`,
        sectionId: figure.id,
      });
    }
  });

  const counts: Record<ValidationSeverity, number> = {
    blocker: 0,
    warning: 0,
    info: 0,
  };
  for (const item of items) counts[item.severity] += 1;

  return {
    canExport: counts.blocker === 0,
    items,
    counts,
  };
}
